import {
  createContext,
  useContext,
  useMemo,
  type ReactNode,
} from "react";
import type { QueryClient } from "@tanstack/react-query";
import { useAuth } from "@clerk/react";
import {
  useGetIntelligenceContext,
  getGetIntelligenceContextQueryKey,
  type IntelligenceSignal,
  type IntelligenceSignalType,
} from "@workspace/api-client-react";

const INTELLIGENCE_QUERY_KEY = getGetIntelligenceContextQueryKey();

/**
 * Single source of truth for the cross-feature intelligence layer on the
 * frontend. Every screen reads the same signals through {@link useIntelligence}
 * (or the convenience {@link useIntelligenceSignals}); React Query dedupes the
 * underlying request by query key, so mounting this provider once at the app
 * root means every dependent screen shares one fetch and updates together.
 *
 * After any mutating action, call {@link invalidateIntelligence} to refresh the
 * context so dependent screens react.
 */

export type { IntelligenceSignal } from "@workspace/api-client-react";

interface IntelligenceValue {
  signals: IntelligenceSignal[];
  generatedAt: string | null;
  isLoading: boolean;
  isError: boolean;
  refetch: () => void;
}

const IntelligenceContext = createContext<IntelligenceValue | null>(null);

export function IntelligenceProvider({ children }: { children: ReactNode }) {
  const { isSignedIn } = useAuth();

  const query = useGetIntelligenceContext({
    query: {
      queryKey: INTELLIGENCE_QUERY_KEY,
      enabled: Boolean(isSignedIn),
      staleTime: 60_000,
      refetchOnWindowFocus: false,
    },
  });

  const value = useMemo<IntelligenceValue>(
    () => ({
      signals: query.data?.signals ?? [],
      generatedAt: query.data?.generatedAt ?? null,
      isLoading: query.isLoading,
      isError: query.isError,
      refetch: () => {
        void query.refetch();
      },
    }),
    [query.data, query.isLoading, query.isError, query.refetch],
  );

  return (
    <IntelligenceContext.Provider value={value}>
      {children}
    </IntelligenceContext.Provider>
  );
}

export function useIntelligence(): IntelligenceValue {
  const value = useContext(IntelligenceContext);
  if (!value) {
    throw new Error(
      "useIntelligence must be used within an <IntelligenceProvider>",
    );
  }
  return value;
}

/**
 * Convenience selector over the shared intelligence context. Filter by signal
 * type(s) and/or a specific metric slug, and optionally cap the count. Returns
 * an empty array when the provider is still loading or errored.
 */
export function useIntelligenceSignals(filter?: {
  types?: IntelligenceSignalType[];
  metricSlug?: string;
  max?: number;
}): IntelligenceSignal[] {
  const { signals } = useIntelligence();
  return useMemo(() => {
    let out = signals;
    if (filter?.types && filter.types.length > 0) {
      const allowed = new Set(filter.types);
      out = out.filter((s) => allowed.has(s.type));
    }
    if (filter?.metricSlug) {
      out = out.filter((s) => s.metricSlug === filter.metricSlug);
    }
    if (filter?.max != null) {
      out = out.slice(0, filter.max);
    }
    return out;
  }, [signals, filter?.types, filter?.metricSlug, filter?.max]);
}

/**
 * Invalidate the shared intelligence context so every dependent screen refetches
 * and updates reactively. Call this after any mutating action (food log, swap
 * accept, grocery add, DNA upload, snapshot, …).
 */
export async function invalidateIntelligence(qc: QueryClient): Promise<void> {
  await qc.invalidateQueries({
    queryKey: INTELLIGENCE_QUERY_KEY,
  });
}
